import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon, SparklesIcon, InfoIcon, XIcon, PlusIcon } from 'lucide-react';
export function Card({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`bg-white border border-slate-200 rounded-2xl p-5 lg:p-6 ${className}`} data-id="element-4364" data-mp="2hoj194lv1vi6">
      {children}
    </div>;
}
export function StepHeading({
  title,
  desc
}: {
  title: string;
  desc?: string;
}) {
  return <div className="mb-6" data-id="element-4365" data-mp="2wmuwr7xhyy7r">
      <h2 className="font-display font-bold text-xl lg:text-2xl text-dark-navy tracking-tight" data-id="element-4366" data-mp="2wm315x3wh0hh">
        {title}
      </h2>
      {desc && <p className="text-sm text-slate-500 mt-1.5 max-w-2xl leading-relaxed" data-id="element-4367" data-mp="31fxp5cqjg1md">
          {desc}
        </p>}
    </div>;
}
export function BlockLabel({
  children,
  hint,
  required
}: {
  children: React.ReactNode;
  hint?: string;
  required?: boolean;
}) {
  return <div className="flex items-center gap-2 mb-2" data-id="element-4368" data-mp="3fe48zoynk131">
      <label className="text-sm font-semibold text-dark-navy" data-id="element-4369" data-mp="1mjkixccfrhgw">{children}</label>
      {required && <span className="px-1.5 py-0.5 rounded-md bg-warning/10 text-[10px] font-bold uppercase tracking-wider text-warning" data-id="element-4370" data-mp="1hjgc6e1by13v">
          Required
        </span>}
      {hint && <span className="text-xs text-slate-400" data-id="element-4371" data-mp="18j9s29b4m2vq">{hint}</span>}
    </div>;
}
export function Field({
  label,
  value,
  onChange,
  placeholder,
  helper,
  type = 'text',
  required
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  helper?: string;
  type?: string;
  required?: boolean;
}) {
  return <div data-id="element-4372" data-mp="1wfxnx6dflhqy">
      <BlockLabel required={required} data-id="element-4373" data-mp="1wfnvlc4wyyi3">{label}</BlockLabel>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-4374" data-mp="1rinqb3fw1zs1" />
      {helper && <p className="text-xs text-slate-400 mt-1.5" data-id="element-4375" data-mp="380tqjgiphn0r">{helper}</p>}
    </div>;
}
export function TextArea({
  label,
  value,
  onChange,
  placeholder,
  helper,
  rows = 4,
  onGenerate,
  generateLabel = 'Write this for me'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  helper?: string;
  rows?: number;
  onGenerate?: () => void;
  generateLabel?: string;
}) {
  return <div data-id="element-4376" data-mp="2pcydjyux4d8p">
      <div className="flex items-center justify-between gap-3 mb-2" data-id="element-4377" data-mp="2p9all454fyw3">
        <label className="text-sm font-semibold text-dark-navy" data-id="element-4378" data-mp="2bdglnambsjv4">{label}</label>
        {onGenerate && <AiButton onClick={onGenerate} label={generateLabel} data-id="element-4379" data-mp="2fe8xmbml12v3" />}
      </div>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out resize-y leading-relaxed" data-id="element-4380" data-mp="9k97w5md2abc" />
      {helper && <p className="text-xs text-slate-400 mt-1.5" data-id="element-4381" data-mp="27vt01xrw6jv1">{helper}</p>}
    </div>;
}
/** The one place Talab's AI identity shows up in the admin: assistive actions. */
export function AiButton({
  onClick,
  label,
  busy
}: {
  onClick: () => void;
  label: string;
  busy?: boolean;
}) {
  return <button type="button" onClick={onClick} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-indigo/[0.06] text-xs font-semibold text-primary-indigo hover:bg-primary-indigo/[0.12] disabled:opacity-60 transition-colors duration-150 ease-out" data-id="element-4382" data-mp="2cvdwa0foge42">
      <SparklesIcon className={`w-3.5 h-3.5 ${busy ? 'animate-pulse' : ''}`} data-id="element-4383" data-mp="rfyd92y7ss12" />
      {busy ? 'Writing…' : label}
    </button>;
}
export function Helper({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100" data-id="element-4384" data-mp="xvfbvke2gdf5">
      <InfoIcon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-4385" data-mp="12ujm8ecq8h72" />
      <p className="text-xs text-slate-500 leading-relaxed" data-id="element-4386" data-mp="12t8k0iyxjhi2">{children}</p>
    </div>;
}
export function Toggle({
  label,
  desc,
  checked,
  onChange
}: {
  label: string;
  desc?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className="flex items-center justify-between gap-4 py-3" data-id="element-4387" data-mp="f68rzu1jdfrr">
      <div className="min-w-0" data-id="element-4388" data-mp="k41uod3mrrna">
        <p className="text-sm font-medium text-dark-navy" data-id="element-4389" data-mp="k2z5qapvbza0">{label}</p>
        {desc && <p className="text-xs text-slate-500 mt-0.5" data-id="element-4390" data-mp="ovk0beglmgel">{desc}</p>}
      </div>
      <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-150 ease-out ${checked ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-4391" data-mp="3nlk0kezueol2">
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-[left] duration-150 ease-out ${checked ? 'left-[22px]' : 'left-0.5'}`} data-id="element-4392" data-mp="3g2sycdfjeoip" />
      </button>
    </div>;
}
export function CheckRow({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return <button type="button" onClick={() => onChange(!checked)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors duration-150 ease-out text-left" data-id="element-4393" data-mp="2xb9xod8zjmju">
      <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border transition-colors duration-150 ease-out ${checked ? 'bg-primary-indigo border-primary-indigo' : 'bg-white border-slate-300'}`} data-id="element-4394" data-mp="2ndlzfdc67wwx">
        {checked && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-4395" data-mp="2ed79u1s6bvnm" />}
      </span>
      <span className="text-sm text-slate-700" data-id="element-4396" data-mp="23q950eqkkir1">{label}</span>
    </button>;
}
/** Visual choice group — the main way merchants make decisions here. */
export function ChoiceCards<T extends string>({
  options,
  value,
  onChange,
  columns = 2
}: {
  options: {
    id: T;
    label: string;
    desc?: string;
    icon?: React.ElementType;
    swatch?: string;
  }[];
  value: T;
  onChange: (v: T) => void;
  columns?: number;
}) {
  return <div className="grid gap-3" style={{
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
  }} data-id="element-4397" data-mp="2nhe0ksmqc5md">
      {options.map(o => {
      const active = o.id === value;
      const Icon = o.icon;
      return <button key={o.id} type="button" onClick={() => onChange(o.id)} className={`relative text-left p-4 rounded-2xl border transition-colors duration-150 ease-out ${active ? 'border-primary-indigo bg-primary-indigo/[0.04]' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-4398" data-mp="2inj9v58piqds">
            {o.swatch && <span className="block w-full h-8 rounded-lg mb-3 border border-black/5" style={{
          background: o.swatch
        }} data-id="element-4399" data-mp="3gic3hq444yg3" />}
            {Icon && <Icon className={`w-5 h-5 mb-2 ${active ? 'text-primary-indigo' : 'text-slate-400'}`} strokeWidth={1.75} data-id="element-4400" data-mp="3pbpa1uv3nykn" />}
            <p className={`text-sm font-semibold ${active ? 'text-primary-indigo' : 'text-dark-navy'}`} data-id="element-4401" data-mp="2xjzrzxpqbchc">
              {o.label}
            </p>
            {o.desc && <p className="text-xs text-slate-500 mt-1 leading-snug" data-id="element-4402" data-mp="36jmuyny3wqci">
                {o.desc}
              </p>}
            {active && <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary-indigo flex items-center justify-center" data-id="element-4403" data-mp="qulbf8so07ju">
                <CheckIcon className="w-3 h-3 text-white" data-id="element-4404" data-mp="2gafg8md6tcni" />
              </span>}
          </button>;
    })}
    </div>;
}
export function Segmented<T extends string>({
  options,
  value,
  onChange
}: {
  options: {
    id: T;
    label: string;
  }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return <div className="inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-4405" data-mp="2u22f8tyikz9z">
      {options.map(o => <button key={o.id} type="button" onClick={() => onChange(o.id)} className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ease-out ${o.id === value ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4406" data-mp="2yzc2c2yfkyun">
          {o.label}
        </button>)}
    </div>;
}
export function Chips({
  items,
  onRemove,
  onAdd,
  addLabel
}: {
  items: string[];
  onRemove: (item: string) => void;
  onAdd: () => void;
  addLabel: string;
}) {
  return <div className="flex flex-wrap items-center gap-2" data-id="element-4407" data-mp="1b2me2oc10f0l">
      {items.map(item => <span key={item} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm font-medium text-slate-700" data-id="element-4408" data-mp="1b4fma82af7ap">
          {item}
          <button type="button" onClick={() => onRemove(item)} aria-label={`Remove ${item}`} className="p-0.5 rounded-full text-slate-400 hover:text-error hover:bg-white transition-colors duration-150 ease-out" data-id="element-4409" data-mp="wdzzwpy4xtay">
            <XIcon className="w-3.5 h-3.5" data-id="element-4410" data-mp="1p0b0w1pnlw8a" />
          </button>
        </span>)}
      <button type="button" onClick={onAdd} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-sm font-medium text-slate-500 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-4411" data-mp="1k1hka0f2lp3w">
        <PlusIcon className="w-3.5 h-3.5" data-id="element-4412" data-mp="4owuv7yfcex9" />
        {addLabel}
      </button>
    </div>;
}
export function StatusPill({
  status
}: {
  status: string;
}) {
  const map: Record<string, string> = {
    verified: 'bg-success/10 text-success',
    pending: 'bg-warning/10 text-warning',
    attention: 'bg-error/10 text-error',
    todo: 'bg-slate-100 text-slate-500'
  };
  const label: Record<string, string> = {
    verified: 'Verified',
    pending: 'Pending',
    attention: 'Needs attention',
    todo: 'Not started'
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${map[status] ?? map.todo}`} data-id="element-4413" data-mp="yo8h3hjrkio5">
      {label[status] ?? status}
    </span>;
}
/** Small, friendly select that avoids a technical dropdown feel. */
export function SimpleSelect({
  label,
  value,
  options,
  onChange,
  helper
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  helper?: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="relative" data-id="element-4414" data-mp="1ox7khvo7kz42">
      <BlockLabel data-id="element-4415" data-mp="1oz0thhmanvf8">{label}</BlockLabel>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 hover:border-slate-300 transition-colors duration-150 ease-out" data-id="element-4416" data-mp="1p025ppf1qnyj">
        {value || 'Select…'}
        <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform duration-150 ease-out ${open ? 'rotate-180' : ''}`} data-id="element-4417" data-mp="166opkqfzsm2z" />
      </button>
      {open && <div className="absolute z-30 left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-large max-h-64 overflow-y-auto p-1" data-id="element-4418" data-mp="x6t9c77ky0w3">
          {options.map(o => <button key={o} type="button" onClick={() => {
        onChange(o);
        setOpen(false);
      }} className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors duration-150 ease-out ${o === value ? 'bg-primary-indigo/[0.06] text-primary-indigo font-semibold' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-4419" data-mp="119y9i346tqhk">
              {o}
              {o === value && <CheckIcon className="w-4 h-4" data-id="element-4420" data-mp="3vk9bp9yy5ctw" />}
            </button>)}
        </div>}
      {helper && <p className="text-xs text-slate-400 mt-1.5" data-id="element-4421" data-mp="r46s7gkqjdpt">{helper}</p>}
    </div>;
}